import { Component, Input, OnInit } from '@angular/core';
import { DatePipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ClrDatepickerModule,
  ClrIconModule,
  ClrInputModule,
  ClrModalModule,
  ClrSelectModule,
} from '@clr/angular';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { ClarityIcons, checkCircleIcon, plusCircleIcon, timesCircleIcon, uploadIcon } from '@cds/core/icon';

import { Companies, Warehouse } from '../../../../_shared/interfaces';
import { AlertService, ContactsStoreService, IAlertType, WarehouseStoreService } from '../../../../_shared/services';
import { getTenantStorage } from '../../../../_shared/utils/tenant-context';
import { filesBaseUrl } from '../../../../_shared/utils/rest-url';

@Component({
  selector: 'app-job-details-warehouse-position',
  standalone: true,
  imports: [
    NgIf, NgForOf, NgClass,
    DatePipe,
    ReactiveFormsModule,
    ClrSelectModule,
    ClrDatepickerModule,
    ClrInputModule,
    ClrIconModule,
    ClrModalModule,
    NgxExtendedPdfViewerModule,
  ],
  templateUrl: './job-details-warehouse-position.component.html',
  styleUrl: './job-details-warehouse-position.component.sass',
})
export class JobDetailsWarehousePositionComponent implements OnInit {
  @Input() projectId = '';
  @Input() jobId = 0;
  @Input() positionId = 0;
  @Input() partId = 0;
  @Input() technicalDrawing = '';

  warehouse: Warehouse[] = [];
  customers: Companies[] = [];
  provisionForm: FormGroup = new FormGroup({});
  loading = true;
  showForm = false;
  showPdf = false;
  modalUrl = '';
  modalContent = '';

  private filesDirectory = filesBaseUrl();

  constructor(
    private waress: WarehouseStoreService,
    private contactss: ContactsStoreService,
    private fb: FormBuilder,
    private alertDispatcher: AlertService,
  ) {
    ClarityIcons.addIcons(plusCircleIcon, checkCircleIcon, timesCircleIcon, uploadIcon);
  }

  ngOnInit(): void {
    this.loadWarehouse();
    this.contactss.getCustomers().subscribe({
      next: data => { this.customers = data?.object ?? []; },
    });
    this.initForm();
  }

  loadWarehouse(): void {
    this.waress.getPositionsWarehouse(this.projectId, this.jobId, 0, this.positionId, this.partId).subscribe({
      next: data => {
        this.warehouse = (data?.object ?? []).filter(w => w.is_provision);
      },
      complete: () => { this.loading = false; },
      error: () => { this.loading = false; },
    });
  }

  initForm(): void {
    this.provisionForm = this.fb.group({
      project_id:        this.projectId,
      job_id:            this.jobId,
      order_id:          0,
      position:          this.positionId,
      part:              this.partId,
      technical_drawing: this.technicalDrawing,
      staff_id:          parseInt(getTenantStorage('staff_id') ?? getTenantStorage('user_id') ?? '0', 10),
      movement_type:     'provision_in',
      is_provision:      true,
      provision_owner:   'internal',
      number_of_pieces:  0,
      date:              '',
      serial_nr:         '',
      heat_nr:           '',
      comment:           '',
    });
  }

  private normalizeDate(value: any): string {
    if (!value) return '';
    if (value instanceof Date) {
      const d = value as Date;
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }
    if (typeof value === 'string') {
      const m = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    }
    return String(value);
  }

  save(): void {
    const raw = { ...this.provisionForm.value };
    raw.date = this.normalizeDate(raw.date);
    this.waress.addToWarehouse(raw).subscribe({
      next: () => {
        this.showForm = false;
        this.loadWarehouse();
        this.alertDispatcher.show(
          { message: `Beistellung für Position ${this.positionId} gespeichert.`, clrAlertType: IAlertType.SUCCESS },
          3000
        );
        this.initForm();
      },
      error: (err: any) => {
        const msg = err?.error?.message ?? 'Beistellung konnte nicht gespeichert werden.';
        this.alertDispatcher.show({ message: msg, clrAlertType: IAlertType.DANGER }, 5000);
      },
    });
  }

  delete(warehouseId: number): void {
    if (!confirm('Wollen Sie diesen Eintrag wirklich löschen?')) return;
    this.waress.deleteFromWarehouse(warehouseId).subscribe({
      next: () => {
        this.loadWarehouse();
        this.alertDispatcher.show(
          { message: 'Beistellungseintrag gelöscht.', clrAlertType: IAlertType.WARNING },
          3000
        );
      },
    });
  }

  openPdf(fileId: string, description: string): void {
    const token = getTenantStorage('access_token');
    this.modalUrl = `${this.filesDirectory}/download_file?file_id=${fileId}&token=${token}`;
    this.modalContent = description || 'Dokument';
    this.showPdf = true;
  }

  movementLabel(type: string): string {
    return type === 'provision_out' ? 'Beistellung Ausgang' : 'Beistellung Eingang';
  }

  movementClass(type: string): string {
    return type === 'provision_out' ? 'label label-blue' : 'label label-blue';
  }

  pieceSign(type: string): string {
    return type === 'provision_out' ? '−' : '+';
  }

  pieceClass(type: string): string {
    return type === 'provision_out' ? 'pieces-out' : 'pieces-in';
  }
}
